import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import styled, { keyframes } from "styled-components";
import "./AnimatedStyle.css";
import eyes from "../image/improvimania/eyes/eyes.png";
import eyesLeft from "../image/improvimania/eyes/eyesleft.png";
import eyesRight from "../image/improvimania/eyes/eyesright.png";
import discoBall from "../image/improvimania/disco.png";

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0) rotate(0deg); }
  40% { transform: translateY(-0.35em) rotate(-1deg); }
  60% { transform: translateY(-0.18em) rotate(1deg); }
`;

const colors = ['#00D9FF', '#6C5CE7', '#FF006E', '#FFD60A'];

const getRandomInt = (max) => Math.floor(Math.random() * max);

const TitleChar = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 clamp(0.5px, 0.25vw, 3px);
  min-width: clamp(0.8rem, 1.8vw, 1.2rem);
  font-family: 'Alloy Ink', 'Patrick Hand', 'Comic Sans MS', cursive, sans-serif;
  font-size: clamp(2.1rem, 8vw, 6.8rem);
  position: relative;
  animation: ${(props) => (props.$bounce ? bounce : 'none')} 900ms ease;
  z-index: 1;
  line-height: 0.9;
  letter-spacing: -0.05em;
  text-transform: uppercase;
  will-change: transform;
  padding: 0.12em 0.15em;
  margin: 0 calc(clamp(0.5px, 0.25vw, 3px) - 0.15em);
  overflow: visible;

  @media (max-width: 480px), (max-aspect-ratio: 9/20) {
    font-size: ${(props) => (props.char === 'i' ? 'clamp(1.7rem, 5vw, 2.6rem)' : 'clamp(2rem, 10vw, 4.2rem)')};
  }

  color: ${props => props.color};
  text-shadow:
    0 2px 0 rgba(0, 0, 0, 0.12),
    0 0 18px ${props => props.glow};
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95) 0%,
    ${props => props.color} 36%,
    ${props => props.accent} 72%,
    rgba(255, 255, 255, 0.9) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.12);
  filter: drop-shadow(0 0 12px ${props => props.glow});
`;

const EyeImage = styled.img`
  width: clamp(36px, 5vw, 72px);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -86%);
  z-index: 2;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
`;

const DiscoBall = styled.img`
  width: clamp(14px, 2vw, 22px);
  position: absolute;
  top: clamp(-18px, -2.2vw, -10px);
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
`;

const TitleContainer = styled.div`
  text-align: center;
  position: relative;
  white-space: nowrap;
  width: 100%;
  padding: clamp(10px, 2vw, 26px);
  margin: 0;
  overflow: visible;
  display: flex;
  justify-content: center;
`;

const AnimatedTitleWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  overflow: visible;   /* was: overflow: hidden */
  max-width: 100%;
`;

const AnimatedLetter = ({ char, index, color, bounce, showEyes, eyePosition, onDiscoBallClick }) => (
  <TitleChar
    color={color}
    accent={colors[(index + 1) % colors.length]}
    glow={`${color}55`}
    $bounce={bounce}
    initial={{ scale: 0, rotate: -180 }}
    animate={{ scale: 1, rotate: 0 }}
    transition={{
      type: "spring",
      stiffness: 180,
      damping: 12,
      delay: index * 0.1,
    }}
  >
    {char}
    {char === 'O' && <EyeImage src={eyePosition} alt="Eyes" $show={showEyes} />}
    {char === 'i' && index === 6 && (
      <DiscoBall src={discoBall} alt="Disco Ball" onClick={onDiscoBallClick} />
    )}
  </TitleChar>
);

const TitleScreen = ({ title = "IMPROViMANIA" }) => {
  const [bouncingLetters, setBouncingLetters] = useState(new Set());
  const [showEyes, setShowEyes] = useState(false);
  const [eyePosition, setEyePosition] = useState(eyes);
  const [partyMode, setPartyMode] = useState(false);

  const togglePartyMode = () => {
    const body = document.body;
    setPartyMode(prev => {
      const next = !prev;
      if (next) {
        body.classList.add('party-mode');
      } else {
        body.classList.remove('party-mode');
      }
      return next;
    });
  };

  useEffect(() => {
    const bounceInterval = setInterval(() => {
      const newSet = new Set();
      newSet.add(getRandomInt(title.length));
      setBouncingLetters(newSet);
    }, 2000);

    const eyesInterval = setInterval(() => {
      setShowEyes(true);
      setEyePosition(eyes);
      setTimeout(() => {
        setEyePosition(eyesLeft);
        setTimeout(() => {
          setEyePosition(eyesRight);
          setTimeout(() => setShowEyes(false), 1000);
        }, 1000);
      }, 1000);
    }, 10000);

    return () => {
      clearInterval(bounceInterval);
      clearInterval(eyesInterval);
    };
  }, [title]);

  const letterProps = useMemo(() => {
    return title.split('').map((char, index) => ({
      char,
      index,
      color: partyMode ? colors[getRandomInt(colors.length)] : colors[index % colors.length],
    }));
  }, [title, partyMode]);

  return (
    <TitleContainer>
        <div className="animatedTitle">
          {letterProps.map(({ char, index, color }) => (
            <AnimatedLetter
              key={index}
              char={char}
              index={index}
              color={color}
              bounce={bouncingLetters.has(index)}
              showEyes={showEyes}
              eyePosition={eyePosition}
              onDiscoBallClick={togglePartyMode}
            />
          ))}
        </div>
    </TitleContainer>
  );
};

const AnimatedTitle = ({ title = "IMPROViMANIA" }) => (
  <div className="AnimatedTitle">
    <TitleScreen title={title} />
  </div>
);

export default AnimatedTitle;